<script lang="ts" setup>
import { Tooltip } from "floating-vue";
import { reactive } from "vue";

enum ACHType {
    Successful = 1,
    Failure = 2,
}

interface DataObject {
    achType: ACHType;
    routingNumber: string;
    isRoutingNumberCopied: boolean;
    routingNumberTooltip: string;
    accountNumber: string;
    isAccountNumberCopied: boolean;
    accountNumberTooltip: string;
    disabledTooltip: boolean;
}

const testAchTypes = [
    {
        text: "ACH - Successful",
        value: ACHType.Successful,
    },
    {
        text: "ACH - Failure",
        value: ACHType.Failure,
    },
];

const data = reactive<DataObject>({
    achType: ACHType.Successful,
    routingNumber: "111000025",
    isRoutingNumberCopied: false,
    routingNumberTooltip: "Copy to clipboard",
    accountNumber: "12121212",
    isAccountNumberCopied: false,
    accountNumberTooltip: "Copy to clipboard",
    disabledTooltip: false,
});

// methods
const copyRoutingNumber = () => {
    navigator.clipboard.writeText(data.routingNumber);
    data.isRoutingNumberCopied = true;
    data.routingNumberTooltip = "Copied!";

    setTimeout(function () {
        data.isRoutingNumberCopied = false;
        data.routingNumberTooltip = "Copy to clipboard";
    }, 2000);
};

const copyAccountNumber = () => {
    navigator.clipboard.writeText(data.accountNumber);
    data.isAccountNumberCopied = true;
    data.accountNumberTooltip = "Copied!";

    setTimeout(function () {
        data.isAccountNumberCopied = false;
        data.accountNumberTooltip = "Copy to clipboard";
    }, 2000);
};

const isSmallerScreen = () => {
    data.disabledTooltip = window.innerWidth < 768;
};

window.addEventListener("resize", isSmallerScreen);
</script>

<template>
    <div class="rd-grid rd-grid-cols-12 rd-gap-x-5 rd-gap-y-3.5 rd-w-full test-payments-ach">
        <!-- ach types -->
        <div class="rd-relative rd-col-span-12 md:rd-col-span-4 md:rd-top-6">
            <label for="testAchs" class="rd-sr-only">
                Test Payment Methods
            </label>

            <select id="testAchs" v-model="data.achType" class="rd-pr-[30px]">
                <option disabled value="">Please select</option>
                <option v-for="item in testAchTypes" :key="item.value" :value="item.value">
                    {{ item.text }}
                </option>
            </select>
        </div>

        <!-- routing -->
        <div class="rd-col-span-12 rd-relative rd-text-left sm:rd-col-span-6 md:rd-col-span-4">
            <label for="routingNumber">Routing</label>

            <input id="routingNumber" readonly type="text" v-model="data.routingNumber" />

            <Tooltip distance="-4" container=".test-payment-types" delay="15" popperClass="tooltip-sm"
                class="rd-absolute rd-right-0 rd-bottom-0" :disabled="data.disabledTooltip">
                <button type="button" class="rd-py-[13px] rd-px-[15px] copy-routing-number" @click="copyRoutingNumber">
                    <i v-if="data.isRoutingNumberCopied" class="fa-solid fa-check rd-text-base rd-leading-none"
                        aria-hidden="true"></i>
                    <i v-else class="fa-solid fa-copy rd-text-base rd-leading-none" aria-hidden="true"></i>
                    <span class="rd-sr-only">Copy Test Routing Number to clipboard</span>
                </button>
                <template #popper>
                    {{ data.routingNumberTooltip }}
                </template>
            </Tooltip>
        </div>

        <!-- account -->
        <div class="rd-col-span-12 rd-relative rd-text-left sm:rd-col-span-6 md:rd-col-span-4">
            <label for="accountNumber">Account</label>

            <input id="accountNumber" readonly type="text" v-model="data.accountNumber" />

            <Tooltip distance="-4" container=".test-payment-types" delay="15" popperClass="tooltip-sm"
                class="rd-absolute rd-right-0 rd-bottom-0" :disabled="data.disabledTooltip">
                <button type="button" class="rd-py-[13px] rd-px-[15px] copy-account-number" @click="copyAccountNumber">
                    <i v-if="data.isAccountNumberCopied" class="fa-solid fa-check rd-text-base rd-leading-none"
                        aria-hidden="true"></i>
                    <i v-else class="fa-solid fa-copy rd-text-base rd-leading-none" aria-hidden="true"></i>
                    <span class="rd-sr-only">Copy Test Account Number to clipboard</span>
                </button>
                <template #popper>
                    {{ data.accountNumberTooltip }}
                </template>
            </Tooltip>
        </div>
    </div>

    <div class="rd-text-sm rd-mt-4" v-if="data.achType === ACHType.Failure">
        <strong>Note:</strong> to test an ACH failure, enter a donation amount greater than $100
    </div>
</template>
